import type { components } from "@majitap/sdk";
import { formatDateTime, formatMoney, isoDay } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { ChevronRight } from "lucide-react";
import { useCompanyFormat } from "../../company";
import { deliveryHref, progressOf } from "../../deliveries";
import { segmentOf } from "../../dispatch";
import { DELIVERY_STATUS } from "../../labels";
import { RouterLink } from "../../router-link";
import styles from "./record.module.css";

type AdminOrder = components["schemas"]["AdminOrder"];
type Delivery = AdminOrder["deliveries"][number];

const SEGMENT_CLASS = { done: styles.segDone, moving: styles.segMoving, failed: styles.segFailed, stopped: "" };

/** What a row says happened last: delivered, cancelled, or when it's planned. */
function whenOf(d: Delivery, format: ReturnType<typeof useCompanyFormat>) {
  if (d.delivered_at) return `Delivered ${formatDateTime(d.delivered_at, format)}`;
  if (d.cancelled_at) return `Cancelled ${formatDateTime(d.cancelled_at, format)}`;
  if (d.scheduled_at) return `Planned ${formatDateTime(d.scheduled_at, format)}`;
  return "As soon as possible";
}

/**
 * The order's deliveries (`Console / Order / Redesign`): one row each, opening its page. A rerouted
 * one is greyed and names the delivery that replaced it; the replacement names where it came from.
 */
export function DeliveriesSection({ order: o }: { order: AdminOrder }) {
  const format = useCompanyFormat();
  const numberOf = (id: string | null) => o.deliveries.find((d) => d.id === id)?.delivery_number ?? null;
  const replacementOf = (d: Delivery) => o.deliveries.find((r) => r.rerouted_from_id === d.id) ?? null;
  const first = o.deliveries[0];
  const board = first
    ? `/dispatch?${new URLSearchParams({
        date: isoDay(format.timezone, new Date(first.scheduled_at ?? o.created_at)),
        segment: segmentOf(first.status),
      })}`
    : "/dispatch";
  return (
    <section className={styles.section} aria-label="Deliveries">
      <div className={styles.sectionHead}>
        <div>
          <h2 className={styles.sectionTitle}>Deliveries</h2>
          <p className={styles.small}>Each truck load is its own delivery. Open one to plan it or follow it.</p>
        </div>
        <RouterLink href={board} className={styles.link}>
          Open on Dispatch
        </RouterLink>
      </div>
      {o.deliveries.length === 0 ? (
        <p className={styles.muted}>No deliveries.</p>
      ) : (
        <div className={styles.panel}>
          {o.deliveries.map((d) => {
            const [label, tone] = DELIVERY_STATUS[d.status];
            const progress = progressOf(d.status);
            const replacement = replacementOf(d);
            const from = numberOf(d.rerouted_from_id);
            const crew = [d.assignment?.truck?.plate_number, d.assignment?.driver?.name, d.assignment?.fillsite?.name]
              .filter(Boolean)
              .join(" · ");
            const stopped = d.status === "rerouted" || d.status === "cancelled";
            return (
              <RouterLink
                key={d.id}
                href={deliveryHref(o.id, d.id)}
                className={`${styles.deliveryRow} ${stopped ? styles.deliveryRowStopped : ""}`}
              >
                <span className={styles.who}>
                  <span className={styles.rowTop}>
                    <span className={styles.code}>{d.delivery_number}</span>
                    <Badge tone={tone}>{label}</Badge>
                    {replacement ? (
                      <span className={styles.small}>Replaced by {replacement.delivery_number}</span>
                    ) : null}
                    {from ? <span className={styles.small}>From {from}</span> : null}
                  </span>
                  <span className={styles.small}>{crew || "Not planned yet"}</span>
                </span>
                <span className={styles.progress}>
                  <span className={styles.bar} role="img" aria-label={`${progress.done} of ${progress.of} steps`}>
                    {Array.from({ length: progress.of }, (_, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: the bar's segments are fixed positions
                      <span key={i} className={i < progress.done ? SEGMENT_CLASS[progress.tone] : ""} />
                    ))}
                  </span>
                  <span className={styles.small}>{whenOf(d, format)}</span>
                </span>
                <span className={`${styles.rowAmount} ${styles.amount}`}>
                  {stopped ? "—" : formatMoney(d.receivable_total, format)}
                </span>
                <ChevronRight size={16} aria-hidden className={styles.small} />
              </RouterLink>
            );
          })}
        </div>
      )}
    </section>
  );
}
